import React, { useMemo, useRef, useState } from 'react';

// Honest daily trend (WOW-4): y-axis gridlines with formatted labels, x-axis
// date ticks (first / mid / last), nearest-point hover tooltip. Fixed internal
// coordinate space; the svg scales to the card width preserving aspect.
// Center port of the admin-console TrendChart — same numeric-trust approach,
// Center tokens, danger ramp (this chart plots lost value).
const W = 1160;
const H = 220;
const PAD_L = 56;
const PAD_R = 14;
const PAD_T = 12;
const PAD_B = 26;

export type TrendPoint = { date: Date; value: number };

interface TrendChartProps {
  points: TrendPoint[];
  formatAxis: (n: number) => string;
  formatValue: (n: number) => string;
  locale: string;
  ariaLabel: string;
}

// React 17 — no useId; a module counter keeps gradient ids unique.
let gradientSeq = 0;

const tickDate = (d: Date, locale: string): string =>
  d.toLocaleDateString(locale === 'ar' ? 'ar-IQ' : 'en-GB', {
    day: 'numeric',
    month: 'short',
  });

const TrendChart: React.FC<TrendChartProps> = ({
  points,
  formatAxis,
  formatValue,
  locale,
  ariaLabel,
}) => {
  const gradIdRef = useRef<string>();
  if (!gradIdRef.current) {
    gradientSeq += 1;
    gradIdRef.current = `failed-trend-grad-${gradientSeq}`;
  }
  const gradId = gradIdRef.current;
  const svgRef = useRef<SVGSVGElement>(null);
  const [hover, setHover] = useState<number | null>(null);

  const values = points.map((p) => p.value);
  const max = Math.max(...values, 1);
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const step = points.length > 1 ? innerW / (points.length - 1) : innerW;

  const coords = useMemo(
    () =>
      values.map<[number, number]>((v, i) => [
        PAD_L + i * step,
        PAD_T + innerH - (v / max) * innerH,
      ]),
    [values, step, innerH, max]
  );

  const path = coords
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`)
    .join(' ');
  const area = `${path} L${(PAD_L + innerW).toFixed(1)},${PAD_T + innerH} L${PAD_L},${
    PAD_T + innerH
  } Z`;

  // y gridlines at 0 / half / max; x ticks at first / mid / last day.
  const yTicks = [0, max / 2, max];
  const xTickIdx = [0, Math.floor((points.length - 1) / 2), points.length - 1];

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const px = ((e.clientX - rect.left) / rect.width) * W;
    const idx = Math.round((px - PAD_L) / step);
    setHover(Math.max(0, Math.min(points.length - 1, idx)));
  };

  const hoverPt = hover != null ? coords[hover] : null;

  return (
    <div style={{ position: 'relative', direction: 'ltr' }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        style={{ display: 'block', width: '100%', height: 'auto' }}
        role='img'
        aria-label={ariaLabel}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          <linearGradient id={gradId} x1='0' y1='0' x2='0' y2='1'>
            <stop offset='0%' stopColor='var(--danger)' stopOpacity='0.16' />
            <stop offset='100%' stopColor='var(--danger)' stopOpacity='0' />
          </linearGradient>
        </defs>

        {yTicks.map((v) => {
          const y = PAD_T + innerH - (v / max) * innerH;
          return (
            <g key={v}>
              <line
                x1={PAD_L}
                x2={W - PAD_R}
                y1={y}
                y2={y}
                stroke='var(--line)'
                strokeWidth='1'
                strokeDasharray={v === 0 ? undefined : '3 4'}
              />
              <text
                x={PAD_L - 8}
                y={y + 3.5}
                textAnchor='end'
                fontSize='11'
                fill='var(--muted)'
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {formatAxis(v)}
              </text>
            </g>
          );
        })}

        {xTickIdx.map((i) => (
          <text
            key={i}
            x={PAD_L + i * step}
            y={H - 8}
            textAnchor={
              i === 0 ? 'start' : i === points.length - 1 ? 'end' : 'middle'
            }
            fontSize='11'
            fill='var(--muted)'
          >
            {tickDate(points[i].date, locale)}
          </text>
        ))}

        <path d={area} fill={`url(#${gradId})`} />
        <path
          d={path}
          fill='none'
          stroke='var(--danger)'
          strokeWidth='1.75'
          strokeLinecap='round'
          strokeLinejoin='round'
        />

        {hoverPt && (
          <>
            <line
              x1={hoverPt[0]}
              x2={hoverPt[0]}
              y1={PAD_T}
              y2={PAD_T + innerH}
              stroke='var(--line-2)'
              strokeWidth='1'
              strokeDasharray='3 3'
            />
            <circle
              cx={hoverPt[0]}
              cy={hoverPt[1]}
              r='4'
              fill='var(--surface)'
              stroke='var(--danger)'
              strokeWidth='2'
            />
          </>
        )}
      </svg>

      {hover != null && hoverPt && (
        <div
          style={{
            position: 'absolute',
            left: `${(hoverPt[0] / W) * 100}%`,
            top: `${(hoverPt[1] / H) * 100}%`,
            transform: 'translate(-50%, calc(-100% - 10px))',
            background: 'var(--brand-ink)',
            color: 'var(--surface)',
            borderRadius: 'var(--r-md)',
            padding: '6px 10px',
            fontSize: 'var(--t-xs)',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          <div style={{ opacity: 0.75 }}>{tickDate(points[hover].date, locale)}</div>
          <div
            style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
          >
            {formatValue(values[hover])}
          </div>
        </div>
      )}
    </div>
  );
};

export default TrendChart;
